import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import BallotSubmissionPage from './components/public/BallotSubmissionPage';
import SecretBallotAdminPage from './components/public/SecretBallotAdminPage';
import PublicLeaderboardPage from './components/public/PublicLeaderboardPage';
import PublicLeaderboardPageLight from './components/public/PublicLeaderboardPageLight';
import PartyResultsPage from './components/public/PartyResultsPage';
import LeaderboardAccessPage from './components/public/LeaderboardAccessPage';
import RequireLeaderboardAccess from './components/public/RequireLeaderboardAccess';
import SimpleHomePage from './components/public/SimpleHomePage';
import AboutPage from './components/public/AboutPage';
import PastWinnersPage from './components/public/PastWinnersPage';
import GalleryPage from './components/public/GalleryPage';
import Header from './components/header/Header';
import Footer from './components/footer/Footer';
import { ScreenContainerProvider } from './utils/ScreenContainerContext';
import { CategoriesProvider } from './contexts/CategoriesContext';
import { BallotsProvider } from './contexts/BallotsContext';
import { BonusQuestionsProvider } from './contexts/BonusQuestionsContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { FirebaseProvider } from './contexts/FirebaseContext';
import { BetaProvider } from './contexts/BetaContext';

function ScrollToTopOnNavigate() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  return null;
}

function App() {
  const location = useLocation();
  const hideHeaderFooterRoutes = new Set(['/leaderboard', '/leaderboardlight', '/secret/access', '/secret/submit', '/beta', '/beta/leaderboard', '/beta/leaderboardlight']);
  const hideHeaderFooter = hideHeaderFooterRoutes.has(location.pathname);

  return (
    <FirebaseProvider>
      <BetaProvider>
        <SettingsProvider>
          <div className="App">
            <ScrollToTopOnNavigate />
            {!hideHeaderFooter && <Header />}
            <Routes>
              {/* V1 Routes - Simple experience */}
              <Route path="/" element={
                <ScreenContainerProvider>
                  <SimpleHomePage />
                </ScreenContainerProvider>
              } />
              <Route path="/submit" element={
                <BonusQuestionsProvider>
                  <ScreenContainerProvider>
                    <BallotSubmissionPage />
                  </ScreenContainerProvider>
                </BonusQuestionsProvider>
              } />
              <Route path="/past-winners" element={
                <ScreenContainerProvider>
                  <PastWinnersPage />
                </ScreenContainerProvider>
              } />
              <Route path="/gallery" element={
                <ScreenContainerProvider>
                  <GalleryPage />
                </ScreenContainerProvider>
              } />
              <Route path="/results" element={
                <ScreenContainerProvider>
                  <PartyResultsPage />
                </ScreenContainerProvider>
              } />
              <Route path="/leaderboard" element={
                <CategoriesProvider>
                  <BallotsProvider>
                    <ScreenContainerProvider>
                      <PublicLeaderboardPage />
                    </ScreenContainerProvider>
                  </BallotsProvider>
                </CategoriesProvider>
              } />
              <Route path="/leaderboardlight" element={
                <CategoriesProvider>
                  <BallotsProvider>
                    <ScreenContainerProvider>
                      <PublicLeaderboardPageLight />
                    </ScreenContainerProvider>
                  </BallotsProvider>
                </CategoriesProvider>
              } />
              <Route path="/secret/access" element={
                <ScreenContainerProvider>
                  <LeaderboardAccessPage />
                </ScreenContainerProvider>
              } />
              <Route path="/secret" element={<Navigate to="/secret/access" replace />} />
              <Route path="/secret/leaderboard" element={<Navigate to="/leaderboard" replace />} />
              <Route path="/secret/leaderboardlight" element={<Navigate to="/leaderboardlight" replace />} />
              <Route element={<RequireLeaderboardAccess />}>
                <Route path="/secret/submit" element={
                  <BallotsProvider>
                    <ScreenContainerProvider>
                      <SecretBallotAdminPage />
                    </ScreenContainerProvider>
                  </BallotsProvider>
                } />
              </Route>

              {/* V2 Beta Routes - Full experience (protected behind secret access) */}
              <Route element={<RequireLeaderboardAccess />}>
                <Route path="/beta" element={
                  <CategoriesProvider>
                    <BallotsProvider>
                      <ScreenContainerProvider>
                        <PublicLeaderboardPage />
                      </ScreenContainerProvider>
                    </BallotsProvider>
                  </CategoriesProvider>
                } />
                <Route path="/beta/leaderboard" element={
                  <CategoriesProvider>
                    <BallotsProvider>
                      <ScreenContainerProvider>
                        <PublicLeaderboardPage />
                      </ScreenContainerProvider>
                    </BallotsProvider>
                  </CategoriesProvider>
                } />
                <Route path="/beta/leaderboardlight" element={
                  <CategoriesProvider>
                    <BallotsProvider>
                      <ScreenContainerProvider>
                        <PublicLeaderboardPageLight />
                      </ScreenContainerProvider>
                    </BallotsProvider>
                  </CategoriesProvider>
                } />
                <Route path="/beta/submit" element={
                  <BonusQuestionsProvider>
                    <ScreenContainerProvider>
                      <BallotSubmissionPage />
                    </ScreenContainerProvider>
                  </BonusQuestionsProvider>
                } />
                <Route path="/beta/about" element={
                  <ScreenContainerProvider>
                    <AboutPage />
                  </ScreenContainerProvider>
                } />
                <Route path="/beta/past-winners" element={
                  <ScreenContainerProvider>
                    <PastWinnersPage />
                  </ScreenContainerProvider>
                } />
                <Route path="/beta/gallery" element={
                  <ScreenContainerProvider>
                    <GalleryPage />
                  </ScreenContainerProvider>
                } />
              </Route>

              {/* Catch-all routes - redirect to home */}
              <Route path="/beta/*" element={<Navigate to="/beta" replace />} />
              <Route path="/secret/*" element={<Navigate to="/secret/access" replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            {!hideHeaderFooter && <Footer />}
          </div>
        </SettingsProvider>
      </BetaProvider>
    </FirebaseProvider>
  );
}

export default App;
